/**
 * Progressive Disclosure Service
 * 
 * Step 4.2: Implement Progressive Disclosure Based on User Stories
 * 
 * Maps actual business claim types (DTH, 010, 012, 013, 002, 004, 005, 006) 
 * to field groups according to parsed user stories.
 * 
 * Evidence from parsed user stories:
 * - Death claims: Death details, burial information
 * - Disability claims: Employment details, medical certificates  
 * - Critical Illness: Simple diagnosis classification
 * 
 * Actual business types from defaults-seed.js:
 * - DTH: Death
 * - 010: Spousal Cover
 * - 012: Coffin Cover
 * - 013: Parent Cover
 * - 002: Critical Illness
 * - 004: Permanent Total Disability (PTD)
 * - 005: Medical
 * - 006: Temporary Total Disability (TTD)
 */

/**
 * Universal fields that appear for ALL claim types
 * Based on user stories: "Show universal fields for all claim types"
 */
const UNIVERSAL_FIELDS = [
  // Policy and member information
  'type',
  'policyNumber', 
  'memberStaffNumber',
  'dateFiled',
  
  // Claimant details
  'claimantName',
  'claimantMobile',
  'claimantEmail', 
  'claimantDateOfBirth',
  'claimantAddress',
  
  // Financial information
  'bankAccountName',
  'bankName',
  'accountNumber',
  'branch',
  'benefitAmount',
  
  // System fields
  'sumAssured',
  'isActive',
  'assignedToId'
];

/**
 * Type-specific field mappings based on user stories and actual business types
 * Updated to use business codes directly (DTH, 010, 012, 013, 002, 004, 005, 006)
 * instead of enum values (DEATH, DISABILITY, etc.)
 */
const TYPE_SPECIFIC_FIELD_GROUPS = {
  // DTH: Death claims (Death/Accidental Death from user stories)
  DTH: {
    category: 'DEATH',
    displayName: 'Death',
    fields: [
      // Death details from user story
      'deceasedName',
      'deceasedDateOfBirth', 
      'deceasedDateOfDeath',
      'placeOfDeath',
      'causeOfDeath',
      'relationshipToDeceased',
      
      // Burial information from user story
      'burialDetails',
      'burialDate',
      'burialLocation', 
      'funeralHomeName',
      'burialCosts'
    ],
    manualReviewFlags: [
      'EXCLUSION_REVIEW', // Potential exclusions require manual review
      'TIMING_REVIEW',    // Submission timing validation
      'DOCUMENT_REVIEW'   // Death certificate validation
    ]
  },

  // 010: Spousal Cover (Marriage/relationship details from user stories)
  '010': {
    category: 'DEATH',
    displayName: 'Spousal Cover',
    fields: [
      // Marriage/relationship details from user story
      // Note: spouseRelationshipVerified and marriageCertificateProvided are auto-ticked based on document uploads
      'relationshipToDeceased',
      
      // Deceased spouse details
      'deceasedName',
      'deceasedDateOfBirth',
      'deceasedDateOfDeath'
    ],
    manualReviewFlags: [
      'RELATIONSHIP_REVIEW', // Marriage verification required
      'DOCUMENT_REVIEW'      // Marriage certificate validation
    ]
  },

  // 012: Coffin Cover (Burial expense details from user stories)
  '012': {
    category: 'DEATH',
    displayName: 'Coffin Cover',
    fields: [
      // Burial expense details from user story
      'funeralExpenseDetails',
      'burialCosts',
      
      // Deceased details
      'deceasedName',
      'deceasedDateOfDeath'
    ],
    manualReviewFlags: [
      'DOCUMENT_REVIEW',  // Funeral receipts validation
      'EXPENSE_REVIEW'    // Expense amount verification
    ]
  },

  // 013: Parent Cover
  '013': {
    category: 'DEATH',
    displayName: 'Parent Cover',
    fields: [
      // Relationship details
      'relationshipToDeceased',
      
      // Deceased parent details
      'deceasedName',
      'deceasedDateOfBirth',
      'deceasedDateOfDeath'
    ],
    manualReviewFlags: [
      'RELATIONSHIP_REVIEW', // Parent relationship verification
      'DOCUMENT_REVIEW'      // Birth certificate validation
    ]
  },

  // 002: Critical Illness (Simple diagnosis classification from user stories)
  '002': {
    category: 'CRITICAL_ILLNESS',
    displayName: 'Critical Illness',
    fields: [
      // Simple diagnosis classification from user story
      'diagnosis',
      'diagnosisDate',
      'illnessStage',
      'conditionSeverityStage',
      
      // Treatment details
      'treatmentRequired',
      'ongoingTreatment'
    ],
    manualReviewFlags: [
      'MEDICAL_REVIEW',    // Medical diagnosis verification
      'PAYOUT_REVIEW'      // Payout percentage validation
    ]
  },

  // 004: Permanent Total Disability (Employment details, medical certificates from user stories)
  '004': {
    category: 'DISABILITY',
    displayName: 'Permanent Total Disability',
    fields: [
      // Employment details from user story
      'dateOfInjury',
      'lastWorkingDate',
      'employmentStatusConfirmed',
      
      // Medical certificates from user story
      'medicalProvider',
      'diagnosis', 
      'diagnosisDate'
    ],
    manualReviewFlags: [
      'MEDICAL_REVIEW',     // Medical assessment required
      'EMPLOYMENT_REVIEW',  // Employment status verification
      'TIMING_REVIEW'       // Injury to claim timing validation
    ]
  },

  // 005: Medical Claims
  '005': {
    category: 'MEDICAL',
    displayName: 'Medical',
    fields: [
      // Medical treatment details
      'medicalTreatmentDetails',
      'medicalInvoiceAmount',
      'medicalProvider',
      'diagnosis',
      'diagnosisDate'
    ],
    manualReviewFlags: [
      'MEDICAL_REVIEW',   // Medical necessity review
      'EXPENSE_REVIEW',   // Medical expense validation
      'DOCUMENT_REVIEW'   // Medical invoices validation
    ]
  },

  // 006: Temporary Total Disability (Employment details, medical certificates from user stories)
  '006': {
    category: 'DISABILITY',
    displayName: 'Temporary Total Disability',
    fields: [
      // Employment details from user story
      'dateOfInjury',
      'lastWorkingDate',
      'employmentStatusConfirmed',
      
      // Medical certificates from user story
      'medicalProvider',
      'diagnosis', 
      'diagnosisDate',
      
      // TTD specific payment details (required by backend)
      'monthlyPaymentAmount',
      'paymentFrequency',
      'paymentStartDate',
      'paymentEndDate',
      'estimatedPaymentDuration'
    ],
    manualReviewFlags: [
      'MEDICAL_REVIEW',     // Medical assessment required
      'EMPLOYMENT_REVIEW',  // Employment status verification
      'TIMING_REVIEW'       // Injury to claim timing validation
    ]
  }
};

/**
 * Get universal fields that appear for all claim types
 * @returns {string[]} Array of universal field names
 */
export const getUniversalFields = () => {
  return [...UNIVERSAL_FIELDS];
};

/**
 * Get type-specific fields for a claim type
 * @param {string} claimType - Business claim type code (DTH, 010, 012, 013, 002, 004, 005, 006)
 * @returns {string[]} Array of type-specific field names
 */
export const getTypeSpecificFields = (claimType) => {
  const fieldGroup = TYPE_SPECIFIC_FIELD_GROUPS[claimType];
  return fieldGroup ? [...fieldGroup.fields] : [];
};

/**
 * Get all visible fields for a claim type (universal + type-specific)
 * @param {string} claimType - Business claim type code
 * @returns {string[]} Array of all visible field names
 */
export const getVisibleFields = (claimType) => {
  if (!claimType) {
    return getUniversalFields();
  }
  
  return [
    ...getUniversalFields(),
    ...getTypeSpecificFields(claimType)
  ];
};

/**
 * Get manual review flags for a claim type
 * @param {string} claimType - Business claim type code
 * @returns {string[]} Array of manual review flag types
 */
export const getManualReviewFlags = (claimType) => {
  const fieldGroup = TYPE_SPECIFIC_FIELD_GROUPS[claimType];
  return fieldGroup ? [...fieldGroup.manualReviewFlags] : [];
};

/**
 * Check if a field requires manual review vs automated validation
 * @param {string} fieldName - Name of the field
 * @returns {boolean} True if field requires manual review
 * @deprecated Manual review indicators have been removed from the UI
 */
export const requiresManualReview = (fieldName) => {
  // Manual review indicators have been removed from the UI
  return false;
};

/**
 * Get display name for a business claim type
 * @param {string} businessType - Business type code (DTH, 010, etc.)
 * @returns {string} Display name or the code if not found
 */
export const getClaimTypeDisplayName = (businessType) => {
  const fieldGroup = TYPE_SPECIFIC_FIELD_GROUPS[businessType];
  return fieldGroup ? fieldGroup.displayName : businessType;
};

/**
 * Get category for a business claim type
 * @param {string} businessType - Business type code (DTH, 010, etc.)
 * @returns {string|null} Category (DEATH, DISABILITY, CRITICAL_ILLNESS, MEDICAL) or null if not found
 */
export const getClaimTypeCategory = (businessType) => {
  const fieldGroup = TYPE_SPECIFIC_FIELD_GROUPS[businessType];
  return fieldGroup ? fieldGroup.category : null;
};

/**
 * Get field sections for progressive disclosure UI
 * @param {string} claimType - Business claim type code
 * @returns {Object} Field sections organized by category
 */
export const getFieldSections = (claimType) => {
  const typeSpecificFields = getTypeSpecificFields(claimType);
  const displayName = getClaimTypeDisplayName(claimType);
  
  return {
    universal: {
      title: 'Universal Information',
      description: 'Basic information required for all claims',
      fields: getUniversalFields()
    },
    typeSpecific: {
      title: `${displayName} Details`,
      description: getTypeSpecificSectionDescription(claimType),
      fields: typeSpecificFields
    }
  };
};

/**
 * Get all fields requiring manual review for a claim type
 * @param {string} claimType - Business claim type code
 * @returns {string[]} Array of field names that require manual review
 * @deprecated Manual review indicators have been removed from the UI
 */
export const getFieldsRequiringManualReview = (claimType) => {
  // Manual review indicators have been removed from the UI
  return [];
};

/**
 * Get complete progressive disclosure configuration for a claim type
 * This method provides the structure expected by Step3ClaimDetails component
 * @param {string} claimType - Business claim type code (DTH, 010, 012, 013, 002, 004, 005, 006)
 * @returns {Object} Complete configuration object
 */
export const getFieldsForClaimType = (claimType) => {
  const universalFields = getUniversalFields();
  const typeSpecificFields = getTypeSpecificFields(claimType);
  const fieldsRequiringManualReview = getFieldsRequiringManualReview(claimType);
  
  // Convert field sections to the format expected by the component
  const fieldSections = {};
  const sections = getFieldSections(claimType);
  
  // Create sections with field arrays (not objects with title/description/fields)
  fieldSections['Universal Information'] = sections.universal.fields;
  fieldSections[sections.typeSpecific.title] = sections.typeSpecific.fields;
  
  return {
    universalFields,
    typeSpecificFields,
    fieldsRequiringManualReview,
    fieldSections
  };
};

/**
 * Get section description for type-specific fields
 * @param {string} claimType - Business claim type code
 * @returns {string} Section description
 */
const getTypeSpecificSectionDescription = (claimType) => {
  const descriptions = {
    DTH: 'Death details and burial information as required by user stories',
    '010': 'Marriage and relationship details as required by user stories',
    '012': 'Funeral expense details as required by user stories',
    '013': 'Parent relationship details',
    '002': 'Simple diagnosis classification as required by user stories',
    '004': 'Employment details and medical certificates as required by user stories', 
    '005': 'Medical treatment and expense details',
    '006': 'Employment details and medical certificates as required by user stories'
  };
  
  return descriptions[claimType] || 'Additional claim-specific information';
};

export const progressiveDisclosureService = {
  getUniversalFields,
  getTypeSpecificFields,
  getVisibleFields,
  getManualReviewFlags,
  requiresManualReview,
  getClaimTypeDisplayName,
  getClaimTypeCategory,
  getFieldSections,
  getFieldsRequiringManualReview,
  getFieldsForClaimType
};

export default progressiveDisclosureService;